import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { fetchRenewOptions, fetchAllRenewServices, calculateRemainingDays } from "./renewAPI";

const initialState = {
  currentService: null,
  hostingServices: [],
  domainServices: [],
  selectedHostingServices: [],
  selectedDomainServices: [],
  totalAmount: 0,
  status: "idle",
  hostingServicesStatus: "idle",
  domainServicesStatus: "idle",
  error: "",
};

export const getRenewOptions = createAsyncThunk(
  "renew/getRenewOptions",
  async (serviceid, { rejectWithValue }) => {
    try {
      const response = await fetchRenewOptions(serviceid);
      
      if (response.status === "error") {
        return rejectWithValue(response.message);
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAllRenewServices = createAsyncThunk(
  "renew/getAllRenewServices",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetchAllRenewServices();
      
      if (response.status === "error") {
        return rejectWithValue(response.message);
      }
      
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const renewSlice = createSlice({
  name: "renew",
  initialState,
  reducers: {
    selectHostingService: (state, action) => {
      const service = action.payload;
      const existingIndex = state.selectedHostingServices.findIndex(
        s => s.serviceid === service.serviceid
      );
      
      if (existingIndex >= 0) {
        // Remove if already selected
        state.selectedHostingServices.splice(existingIndex, 1);
        
        // Remove related domains if this hosting was removed
        if (service.related_domains && service.related_domains.length > 0) {
          service.related_domains.forEach(domain => {
            const domainIndex = state.selectedDomainServices.findIndex(d => d.domainid === domain.domainid);
            if (domainIndex >= 0) {
              state.selectedDomainServices.splice(domainIndex, 1);
            }
          });
        }
      } else {
        // Add if not selected with default billing cycle
        const serviceWithBillingCycle = {
          ...service,
          selectedBillingCycle: service.billingcycle,
          selectedPrice: service.amount || service.renew_price
        };
        state.selectedHostingServices.push(serviceWithBillingCycle);
      }
      
      // Recalculate total amount
      state.totalAmount = calculateTotalAmount(state.selectedHostingServices, state.selectedDomainServices);
    },
    selectDomainService: (state, action) => {
      const service = action.payload;
      const existingIndex = state.selectedDomainServices.findIndex(
        s => s.domainid === service.domainid
      );
      
      if (existingIndex >= 0) {
        // Remove if already selected
        state.selectedDomainServices.splice(existingIndex, 1);
      } else {
        // Add if not selected with default period
        const serviceWithPeriod = {
          ...service,
          selectedPeriod: service.regperiod || 1,
          selectedPrice: service.amount || service.renew_price
        };
        state.selectedDomainServices.push(serviceWithPeriod);
      }
      
      // Recalculate total amount
      state.totalAmount = calculateTotalAmount(state.selectedHostingServices, state.selectedDomainServices);
    },
    selectAllRelatedDomains: (state, action) => {
      const hostingService = action.payload;
      const relatedDomains = hostingService.related_domains || [];
      
      // Add all renewable related domains (don't clear existing selections)
      relatedDomains.forEach(domain => {
        if (domain.can_renew) {
          // Check if domain is already selected
          const existingIndex = state.selectedDomainServices.findIndex(d => d.domainid === domain.domainid);
          if (existingIndex === -1) { // Only add if not already selected
            const domainWithPeriod = {
              ...domain,
              selectedPeriod: domain.regperiod || 1,
              selectedPrice: domain.amount || domain.renew_price
            };
            state.selectedDomainServices.push(domainWithPeriod);
          }
        }
      });
      
      // Recalculate total amount
      state.totalAmount = calculateTotalAmount(state.selectedHostingServices, state.selectedDomainServices);
    },
    clearSelections: (state) => {
      state.selectedHostingServices = [];
      state.selectedDomainServices = [];
      state.totalAmount = 0;
    },
    updateHostingBillingCycle: (state, action) => {
      const { serviceid, billingcycle, price } = action.payload;
      const serviceIndex = state.selectedHostingServices.findIndex(s => s.serviceid === serviceid);
      
      if (serviceIndex >= 0) {
        state.selectedHostingServices[serviceIndex].selectedBillingCycle = billingcycle;
        state.selectedHostingServices[serviceIndex].selectedPrice = price;
        state.totalAmount = calculateTotalAmount(state.selectedHostingServices, state.selectedDomainServices);
      }
    },
    updateDomainPeriod: (state, action) => {
      const { domainid, period, price } = action.payload;
      const domainIndex = state.selectedDomainServices.findIndex(d => d.domainid === domainid);
      
      if (domainIndex >= 0) {
        state.selectedDomainServices[domainIndex].selectedPeriod = period;
        state.selectedDomainServices[domainIndex].selectedPrice = price;
        state.totalAmount = calculateTotalAmount(state.selectedHostingServices, state.selectedDomainServices);
      }
    },
    clearRenewData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRenewOptions.pending, (state) => {
        state.status = "loading";
        state.error = "";
      })
      .addCase(getRenewOptions.fulfilled, (state, action) => {
        state.status = "idle";
        state.currentService = action.payload.current_service;
        
        // Reset selections
        state.selectedHostingServices = [];
        state.selectedDomainServices = [];
        state.totalAmount = 0;
      })
      .addCase(getRenewOptions.rejected, (state, action) => {
        state.status = "error";
        state.error = action.payload || "Failed to fetch renew options";
        state.currentService = null;
      })
      .addCase(getAllRenewServices.pending, (state) => {
        state.hostingServicesStatus = "loading";
        state.domainServicesStatus = "loading";
        state.error = "";
      })
      .addCase(getAllRenewServices.fulfilled, (state, action) => {
        state.hostingServicesStatus = "idle";
        state.domainServicesStatus = "idle";
        state.hostingServices = action.payload.hosting_services || [];
        state.domainServices = action.payload.domain_services || [];
        
        // Reset selections
        state.selectedHostingServices = [];
        state.selectedDomainServices = [];
        state.totalAmount = 0;
      })
      .addCase(getAllRenewServices.rejected, (state, action) => {
        state.hostingServicesStatus = "error";
        state.domainServicesStatus = "error";
        state.error = action.payload || "Failed to fetch renew services";
        state.hostingServices = [];
        state.domainServices = [];
      });
  },
});

// Helper function to calculate total amount
function calculateTotalAmount(hostingServices, domainServices) {
  const hostingTotal = hostingServices.reduce((sum, service) => {
    const price = parseFloat(service.selectedPrice || service.amount || service.renew_price || 0);
    return sum + (isNaN(price) ? 0 : price);
  }, 0);
  
  const domainTotal = domainServices.reduce((sum, service) => {
    const price = parseFloat(service.selectedPrice || service.amount || service.renew_price || 0);
    return sum + (isNaN(price) ? 0 : price);
  }, 0);
  
  const total = hostingTotal + domainTotal;
  return isNaN(total) ? 0 : total;
}

export const {
  selectHostingService,
  selectDomainService,
  selectAllRelatedDomains,
  clearSelections,
  updateHostingBillingCycle,
  updateDomainPeriod,
  clearRenewData,
} = renewSlice.actions;

export default renewSlice.reducer;
